/**
 * AssistantAdminPanel — Plan 05-08b.
 *
 * Admin AI cost and usage panel per UI-SPEC §Component 8.
 * Står i AdminDashboardPanel; useAiUsage spør bare for voksne (admin-rolle).
 *
 * #159 (redesign steg 9c, Stell_Admin): kortet «AI i dag».
 *   AI I DAG                    0,34 USD
 *   N svar
 *   Mamma   200 omganger/dag · 3 brukt      0,21 USD
 *
 * Alt i kortet er dagens tall: /api/admin/ai/usage teller fra midnatt i Oslo
 * når `since` mangler. Skissens «kr 3,40 av kr 10» med fremdriftslinje er
 * utelatt, fordi kostnaden finnes bare i USD og det ikke finnes noe
 * dagsbudsjett. Skissens fliser for allergirettinger og morgenhilsener er
 * tellere siden oppstart, ikke for i dag, så de ligger under «AI-drift».
 *
 * Tall: `.tnum`, desimalkomma (nb-NO Intl.NumberFormat). Kostnaden står i
 * USD — en skjerm bare for voksne, der nøyaktighet teller.
 *
 * AI-21 (#193): under kortet står bryteren for assistentens hukommelse
 * (`families.ai_memory_enabled`, GET/PUT /api/ai/memories/settings). Av = ingen
 * remember/forget-verktøy og ingen notatblokk i prompten; notatene beholdes.
 * PUT krever fersk admin-scope, så en utløpt opplåsing åpner AdminReauthModal
 * og prøver igjen, som de andre adminhandlingene.
 *
 * T-05-08b-AUDIT-PANEL-PII: admin panel is admin-only (requireAdmin server-side);
 * names are short_name (family-public via leaderboard); cost_usd is admin-only.
 */
import { useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorState, LoadingState, Switch } from '../../components/primitives';
import { useMemorySettings, useSetMemoryEnabled } from '../assistant/useMemories';
import { useAiUsage } from './useAiUsage';
import { AdminReauthModal } from './AdminReauthModal';
import { isAdminScopeError } from './useMembers';
import { ADMIN_CARD, ADMIN_LABEL } from './adminStyles';

const fmtUsd = new Intl.NumberFormat('nb-NO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Bryteren for `ai_memory_enabled` (#193). Standard er på (beslutning 4 i #190). */
function MemorySwitchCard(): ReactElement {
  const { t } = useTranslation();
  const settings = useMemorySettings();
  const setEnabled = useSetMemoryEnabled();
  const [reauthFor, setReauthFor] = useState<boolean | null>(null);
  const labelId = useId();

  function save(next: boolean): void {
    setEnabled.mutate(next, {
      onError: (err) => {
        if (isAdminScopeError(err)) setReauthFor(next);
      },
    });
  }

  const failed = setEnabled.isError && !isAdminScopeError(setEnabled.error);

  return (
    <section data-testid="ai-memory-settings" aria-labelledby={labelId} className={`${ADMIN_CARD} flex flex-col gap-1`}>
      <div className="flex items-center justify-between gap-3">
        <h2 id={labelId} className={ADMIN_LABEL}>
          {t('assistant.memory.admin.heading')}
        </h2>
        {settings.data ? (
          <Switch
            checked={settings.data.enabled}
            label={t('assistant.memory.admin.switch_label')}
            data-testid="ai-memory-switch"
            disabled={setEnabled.isPending}
            onChange={save}
          />
        ) : null}
      </div>
      {settings.isPending ? <LoadingState lines={1} /> : null}
      {settings.isError ? (
        <ErrorState
          variant="inline"
          heading={t('assistant.memory.admin.load_error')}
          onRetry={() => void settings.refetch()}
        />
      ) : null}
      <p className="m-0 text-caption text-ink-2">
        {t('assistant.memory.admin.body')}
      </p>
      {failed ? (
        <p role="alert" className="m-0 text-xs text-bad">
          {t('assistant.memory.admin.save_error')}
        </p>
      ) : null}
      <AdminReauthModal
        open={reauthFor !== null}
        onClose={() => setReauthFor(null)}
        onSuccess={() => {
          const next = reauthFor;
          setReauthFor(null);
          if (next !== null) save(next);
        }}
      />
    </section>
  );
}

export function AssistantAdminPanel(): ReactElement {
  return (
    <>
      <AiTodayCard />
      <MemorySwitchCard />
    </>
  );
}

function AiTodayCard(): ReactElement {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useAiUsage();
  const headingId = useId();
  const usd = (amount: number) => t('assistant.admin.cost_usd', { amount: fmtUsd.format(amount) });

  // Kortet står mens dataene lastes, så siden ikke hopper. `ai-admin-panel`
  // finnes først når dataene er der (testkontrakten «absent while loading»).
  if (isLoading) {
    return (
      <div className={ADMIN_CARD}>
        <LoadingState lines={3} />
      </div>
    );
  }

  // Feilet (eller avslått for en som ikke er voksen): vis det, ikke et
  // skjelett som aldri blir ferdig.
  if (!data) {
    return (
      <div className={ADMIN_CARD}>
        <ErrorState
          variant="inline"
          heading={t('admin.ai_today.load_error')}
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  return (
    <div className={ADMIN_CARD}>
      <section
        data-testid="ai-admin-panel"
        aria-labelledby={headingId}
        className="flex flex-col gap-2"
      >
        <div className="flex items-baseline justify-between gap-3">
          <h2 id={headingId} className={ADMIN_LABEL}>
            {t('admin.ai_today.heading')}
          </h2>
          <span data-testid="ai-cost-today" className="tnum text-sm font-extrabold text-ink">
            {usd(data.totals.cost_usd)}
          </span>
        </div>
        <p className="tnum m-0 text-caption font-bold text-ink-2">
          {t('admin.ai_today.turns', { count: data.totals.turns })}
        </p>

        <ul className="m-0 list-none p-0">
          {data.by_member.map((m) => (
            <li
              key={m.member_id}
              className="flex min-h-11 items-center gap-3 border-t border-card-line py-1.5"
            >
              <div className="min-w-0 flex-1">
                <p className="m-0 truncate text-sm font-extrabold text-ink">{m.name}</p>
                <p className="tnum m-0 text-caption text-ink-2">
                  {t('assistant.admin.turns_per_day', { limit: m.daily_limit })} ·{' '}
                  {t('assistant.admin.turns_used', { used: m.today_used })}
                </p>
              </div>
              <span className="tnum shrink-0 text-caption font-bold text-ink-2">{usd(m.cost_usd)}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
